import React, { useState } from "react";
import { Modal, Box, CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonDatePicker from "components/ArgonDatePicker";
import { sendNotification, scheduleNotification } from "services/notificationService";
import { toast } from "react-toastify";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloseIcon from "@mui/icons-material/Close";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 600,
  maxWidth: "90vw",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "16px",
  py: 5,
  px: 5,
  overflow: "auto",
};

function NotificationModal({ open, handleClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    scheduledAt: null,
  });
  const [isScheduled, setIsScheduled] = useState(false);

  // Helper function to count words
  const countWords = (text) => {
    if (!text || text.trim() === "") return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Word limit validation
    if (name === "title") {
      const wordCount = countWords(value);
      if (wordCount > 20) {
        toast.error("Title cannot exceed 20 words");
        return;
      }
    } else if (name === "description") {
      const wordCount = countWords(value);
      if (wordCount > 50) {
        toast.error("Description cannot exceed 50 words");
        return;
      }
    }

    handleChange(name, value);
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length > 0) {
      handleChange("scheduledAt", dates[0]);
    } else {
      handleChange("scheduledAt", null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate title
    const titleWordCount = countWords(form.title);
    if (!form.title || titleWordCount === 0) {
      toast.error("Title is required");
      return;
    }
    if (titleWordCount > 20) {
      toast.error("Title cannot exceed 20 words");
      return;
    }

    // Validate description
    const descriptionWordCount = countWords(form.description);
    if (!form.description || descriptionWordCount === 0) {
      toast.error("Description is required");
      return;
    }
    if (descriptionWordCount > 50) {
      toast.error("Description cannot exceed 50 words");
      return;
    }

    if (isScheduled && !form.scheduledAt) {
      toast.error("Please select a scheduled date and time");
      return;
    }

    // Check if scheduled time is in the past
    if (isScheduled && form.scheduledAt) {
      const scheduledDate = new Date(form.scheduledAt);
      const now = new Date();
      if (scheduledDate <= now) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }

    setLoading(true);
    const payload = {
      title: form.title,
      description: form.description,
    };

    if (isScheduled && form.scheduledAt) {
      payload.scheduledAt = new Date(form.scheduledAt).toISOString();
    }

    try {
      if (isScheduled) {
        await scheduleNotification(payload);
        toast.success("Notification scheduled successfully");
      } else {
        await sendNotification(payload);
        toast.success("Notification sent successfully");
      }
      onUpdate();
      setLoading(false);
      handleClose();
      // Reset form
      setForm({
        title: "",
        description: "",
        scheduledAt: null,
      });
      setIsScheduled(false);
    } catch (err) {
      console.error("Error submitting notification:", err);
      toast.error(
        err.response?.data?.message || "Failed to send notification"
      );
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setForm({
      title: "",
      description: "",
      scheduledAt: null,
    });
    setIsScheduled(false);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={4}
          >
            <ArgonBox
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              }}
            >
              <SendIcon sx={{ color: "white", fontSize: "28px" }} />
            </ArgonBox>
            <ArgonTypography variant="h4" fontWeight="bold" mb={1}>
              Send Notification
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text.secondary" textAlign="center">
              Send push notifications to all users or schedule for later
            </ArgonTypography>
          </ArgonBox>

          <ArgonBox mb={2}>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <ArgonTypography variant="h6" fontWeight="bold">
                Title <span style={{ color: "red" }}>*</span>
              </ArgonTypography>
              <ArgonTypography
                variant="caption"
                sx={{
                  color: countWords(form.title) > 20 ? "#f44336" : "#7B809A",
                  fontWeight: 500
                }}
              >
                {countWords(form.title)}/20 words
              </ArgonTypography>
            </ArgonBox>
            <ArgonInput
              name="title"
              value={form.title}
              onChange={handleInputChange}
              fullWidth
              disabled={loading}
              placeholder="Enter notification title (max 20 words)"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: countWords(form.title) > 20 ? "#f44336" : "#3b98d1",
                  },
                },
              }}
            />
          </ArgonBox>

          <ArgonBox mb={2}>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <ArgonTypography variant="h6" fontWeight="bold">
                Description <span style={{ color: "red" }}>*</span>
              </ArgonTypography>
              <ArgonTypography
                variant="caption"
                sx={{
                  color: countWords(form.description) > 50 ? "#f44336" : "#7B809A",
                  fontWeight: 500
                }}
              >
                {countWords(form.description)}/50 words
              </ArgonTypography>
            </ArgonBox>
            <ArgonInput
              name="description"
              value={form.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              disabled={loading}
              placeholder="Enter notification message (max 50 words)"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: countWords(form.description) > 50 ? "#f44336" : "#3b98d1",
                  },
                },
              }}
            />
          </ArgonBox>

          <ArgonBox mb={3}>
            <ArgonBox display="flex" alignItems="center" mb={2}>
              <input
                type="checkbox"
                id="schedule"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                disabled={loading}
                style={{ marginRight: "8px", cursor: "pointer" }}
              />
              <ArgonTypography
                variant="h6"
                fontWeight="bold"
                style={{ cursor: "pointer" }}
                onClick={() => !loading && setIsScheduled(!isScheduled)}
              >
                Schedule for later
              </ArgonTypography>
            </ArgonBox>

            {isScheduled && (
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                  Scheduled Date & Time <span style={{ color: "red" }}>*</span>
                </ArgonTypography>
                <ArgonDatePicker
                  value={form.scheduledAt ? [form.scheduledAt] : null}
                  onChange={handleDateChange}
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i",
                    minDate: "today",
                    time_24hr: false,
                    allowInput: true,
                    clickOpens: true,
                    minuteIncrement: 1,
                  }}
                  disabled={loading}
                />
                <ArgonTypography
                  variant="caption"
                  sx={{
                    color: "#7B809A",
                    mt: 1,
                    display: "block"
                  }}
                >
                  Select a future date and time for scheduling (you can type the time manually)
                </ArgonTypography>
              </ArgonBox>
            )}
          </ArgonBox>

          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mt={4}
            pt={3}
            sx={{
              borderTop: "1px solid #e0e0e0",
              flexDirection: { xs: "column", sm: "row" }
            }}
          >
            <ArgonButton
              variant="outlined"
              onClick={handleCloseModal}
              type="button"
              disabled={loading}
              startIcon={<CloseIcon />}
              sx={{
                minWidth: "120px",
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "#7B809A",
                color: "#7B809A",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#5a5f73",
                  backgroundColor: "rgba(123, 128, 154, 0.04)",
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
            >
              Cancel
            </ArgonButton>

            <ArgonButton
              variant="contained"
              type="submit"
              disabled={
                loading ||
                !form.title ||
                !form.description ||
                countWords(form.title) === 0 ||
                countWords(form.title) > 20 ||
                countWords(form.description) === 0 ||
                countWords(form.description) > 50 ||
                (isScheduled && !form.scheduledAt)
              }
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : isScheduled ? (
                  <ScheduleIcon />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                minWidth: isScheduled ? "200px" : "160px",
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: 700,
                borderRadius: "8px",
                background: isScheduled
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "white",
                textTransform: "none",
                boxShadow: isScheduled
                  ? "0 4px 15px rgba(102, 126, 234, 0.4)"
                  : "0 4px 15px rgba(17, 153, 142, 0.4)",
                "&:hover": {
                  background: isScheduled
                    ? "linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)"
                    : "linear-gradient(135deg, #0d7a70 0%, #2dd66a 100%)",
                  boxShadow: isScheduled
                    ? "0 6px 20px rgba(102, 126, 234, 0.5)"
                    : "0 6px 20px rgba(17, 153, 142, 0.5)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
                "&:disabled": {
                  background: "#e0e0e0",
                  color: "#9e9e9e",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading
                ? "Processing..."
                : isScheduled
                  ? "Schedule Notification"
                  : "Send Now"}
            </ArgonButton>
          </ArgonBox>
        </form>
      </Box>
    </Modal>
  );
}

export default NotificationModal;
